/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useEffect } from "react";
import { DatabaseRow } from "@Shape-Digital/kudzu-data/lib/types/common";
import useDataState from "../../../hooks/useDataState";
import { useSupabaseClient } from "../../../common/supabaseClient";

export type SupabaseBookingAddOn = Pick<
  DatabaseRow<"center_add_ons">,
  | "id"
  | "name"
  | "type"
  | "price"
  | "applies_to"
  | "marketing_text"
  | "is_per_study_pricing"
  | "allow_multiple_purchases"
>;

const supabaseBookingAddOnsRequest = `
id,
name,
type,
applies_to,
price,
visibility,
marketing_text,
is_per_study_pricing,
allow_multiple_purchases
`;

export type AddOnAppliesTo = "study" | "appointment";

export type BookingAddOn = {
  id: string;
  name: string;
  type: string;
  price: number;
  unitPrice: number;
  appliesTo: AddOnAppliesTo;
  marketingText?: string | null;
  isPerStudyPricing: boolean;
  allowMultiplePurchases: boolean;
};

export const parseAddOn = (addOn: SupabaseBookingAddOn): BookingAddOn => {
  return {
    id: addOn.id,
    name: addOn.name,
    type: addOn.type,
    unitPrice: addOn.price,
    price: addOn.price,
    appliesTo: (addOn.applies_to || "appointment") as AddOnAppliesTo,
    marketingText: addOn.marketing_text,
    isPerStudyPricing: !!addOn.is_per_study_pricing,
    allowMultiplePurchases: !!addOn.allow_multiple_purchases,
  };
};

const useBookingAddOns = (centerId?: string, isCenterUser?: boolean) => {
  const supabase = useSupabaseClient();

  const { dataState, updateDataState } = useDataState<BookingAddOn[]>();

  const getAddOnsRequest = useCallback(async () => {
    try {
      if (!centerId || !supabase) return;

      updateDataState({ status: "loading" });

      const visibility: Array<"public" | "private"> = isCenterUser
        ? ["public", "private"]
        : ["public"];

      const response = await supabase
        .from("center_add_ons")
        .select(supabaseBookingAddOnsRequest)
        .eq("center_id", centerId)
        .in("visibility", visibility)
        .returns<DatabaseRow<"center_add_ons">[]>();

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (response.data) {
        const parsedAddOns = response.data.map(parseAddOn);
        updateDataState({ status: "success", data: parsedAddOns });
      }
    } catch (err) {
      updateDataState({ status: "error", error: (err as Error).message });
    }
  }, [centerId, isCenterUser, supabase, updateDataState]);

  useEffect(() => {
    getAddOnsRequest();
  }, [getAddOnsRequest]);

  return dataState;
};

export default useBookingAddOns;
